import type {
  ApiResponse,
  AuthResponse,
  Category,
  Product,
  ClientMe,
  Me,
  FranchiseMe,
  DirectoryShop,
  OrderShort,
  OrderCreated,
  RepresentativeType,
  PrivacyStatus,
  DepositAccount,
  DepositTransaction,
  DepositInvoice,
  OrderDetail,
  Claim,
  ClaimFilePayload,
  ActEntity,
  DepositAct,
  AppNotification,
  NotificationList,
  NotificationKindPrefs,
  NotificationChannel,
  TelegramLinkCode,
} from './types';

let _token: string | null = localStorage.getItem('gh_token');

export function setToken(t: string): void {
  _token = t;
  localStorage.setItem('gh_token', t);
}

// Этап W1 (вариант B): выход из веб-кабинета и сброс протухшего токена.
// Чистим и память, и localStorage, чтобы при перезагрузке не «зависнуть» на
// невалидной сессии.
export function clearToken(): void {
  _token = null;
  localStorage.removeItem('gh_token');
}

export function hasToken(): boolean {
  return !!_token;
}

function headers(): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (_token) h['Authorization'] = `Bearer ${_token}`;
  return h;
}

async function req<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...opts,
    headers: { ...headers(), ...(opts?.headers as Record<string, string> ?? {}) },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

// ── Auth ─────────────────────────────────────────────────────────────────────

export const authTelegram = (initData: string) =>
  req<ApiResponse<AuthResponse>>('/api/v1/auth/telegram', {
    method: 'POST',
    body: JSON.stringify({ init_data: initData }),
  });

// Этап 16.I: текущий пользователь с ролями — чтобы распознать boss.
export const getMe = () => req<ApiResponse<Me>>('/api/v1/auth/me');

// ── Веб-аутентификация (Этап W1, вариант B — резервный канал) ─────────────────
// Эти эндпоинты используются, когда SPA открыт в обычном браузере (вне Telegram):
// регистрация по e-mail+паролю, вход, сброс пароля. Подтверждение e-mail —
// по ссылке из письма (страницу рисует бэкенд).

export interface WebRegisterBody {
  representative_type: RepresentativeType;
  last_name: string;
  first_name: string;
  email: string;
  phone: string;
  password: string;
  wished_shop_id?: string;
  wished_shop_text?: string;
  privacy_accepted: boolean;
}

export const webRegister = (body: WebRegisterBody) =>
  req<ApiResponse<AuthResponse>>('/api/v1/auth/register', {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const webLogin = (email: string, password: string) =>
  req<ApiResponse<AuthResponse>>('/api/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

export const requestPasswordReset = (email: string) =>
  req<ApiResponse<{ message: string }>>('/api/v1/auth/password-reset/request', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });

export const confirmPasswordReset = (token: string, newPassword: string) =>
  req<ApiResponse<{ message: string }>>('/api/v1/auth/password-reset/confirm', {
    method: 'POST',
    body: JSON.stringify({ token, new_password: newPassword }),
  });

// Этап W1 (вариант B): повторная отправка письма-подтверждения e-mail.
// Используется кнопкой на экране-турникете, если письмо не дошло.
export const resendConfirmation = () =>
  req<ApiResponse<{ message: string; already_verified?: boolean }>>(
    '/api/v1/auth/resend-confirmation',
    { method: 'POST' },
  );

// Этап 30: «турникет» — подтверждение согласия с актуальной версией Политики ПДн.
// После успеха privacy.needs_acceptance станет false (блокирующий экран исчезает).
export const acceptPrivacy = () =>
  req<ApiResponse<{ privacy: PrivacyStatus }>>('/api/v1/privacy/accept', {
    method: 'POST',
  });

// Публичные настройки для клиента (минимальная сумма заказа и т.п.).
export interface PublicSettings {
  min_order_amount: string; // руб., строкой
  // Блок B: сумма пополнения депозита по умолчанию (руб., строкой).
  deposit_default_amount?: string;
}
export const getPublicSettings = () =>
  req<ApiResponse<PublicSettings>>('/api/v1/settings/public');

// Этап 17: контекст кабинета франшизы (boss / network_manager).
export const getFranchiseMe = () => req<ApiResponse<FranchiseMe>>('/api/v1/franchise/me');

// ── Каталог ───────────────────────────────────────────────────────────────────

export const getCategories = () =>
  req<ApiResponse<Category[]>>('/api/v1/categories');

export const getProducts = (categoryId?: string) =>
  req<ApiResponse<Product[]>>(
    `/api/v1/products${categoryId ? `?category_id=${categoryId}` : ''}`,
  );

// ── Клиент ────────────────────────────────────────────────────────────────────

export const getClientMe = () =>
  req<ApiResponse<ClientMe>>('/api/v1/clients/me');

export interface RegisterBody {
  // Этап 20: тип представителя. 'greenhouse' — представитель GreenHouse (клиент,
  // может указать пожелание по точке); 'supplier' — представитель поставщика
  // (только контакты, без точки и роли).
  representative_type: RepresentativeType;
  // Этап 15.1: ФИО + контакты контактного лица — обязательные поля верхнего уровня.
  last_name: string;
  first_name: string;
  email: string;
  phone: string;
  // Этап W1 (вариант B): пароль для резервного входа через сайт (если Telegram
  // недоступен). Бэкенд хеширует и сохраняет; минимум 8 символов.
  password: string;
  // Этап 19.3: упрощённая регистрация. Реквизиты юрлица больше НЕ вводятся (их
  // заполняет Админ в карточке точки). Точка не «занимается» — клиент лишь
  // НЕОБЯЗАТЕЛЬНО оставляет пожелание: id выбранной из справочника точки
  // (wished_shop_id) ИЛИ свободный текст (wished_shop_text). Привязку к точке
  // делает Админ при одобрении заявки (этап 19.4). Только для 'greenhouse'.
  wished_shop_id?: string;
  wished_shop_text?: string;
  // Этап 29 (152-ФЗ): согласие на обработку ПДн. Обязательно true — иначе бэкенд
  // вернёт 422. На фронте кнопка «Отправить» заблокирована без галочки.
  privacy_accepted: boolean;
}

export const registerClient = (body: RegisterBody) =>
  req<ApiResponse<unknown>>('/api/v1/clients/register', {
    method: 'POST',
    body: JSON.stringify(body),
  });

// Этап 17.4: свободные точки справочника «ВОЗИМ» для выбора при регистрации.
export const getDirectoryShops = () =>
  req<ApiResponse<DirectoryShop[]>>('/api/v1/coffee-shops/directory');

// Этап 17.4: «Не нашли нужную кофейню?» — заявка админу добавить точку вручную.
export const requestNewShop = (body: { name: string; address: string; comment?: string }) =>
  req<ApiResponse<unknown>>('/api/v1/coffee-shops/request-new', {
    method: 'POST',
    body: JSON.stringify(body),
  });

// ── Корзина (синхронизация с бэкендом) ───────────────────────────────────────

export const upsertCartItem = (coffeeShopId: string, productId: string, quantity: number) =>
  req<ApiResponse<unknown>>(`/api/v1/cart/items?coffee_shop_id=${coffeeShopId}`, {
    method: 'PUT',
    body: JSON.stringify({ product_id: productId, quantity }),
  });

export const clearBackendCart = (coffeeShopId: string) =>
  req<ApiResponse<unknown>>(`/api/v1/cart?coffee_shop_id=${coffeeShopId}`, { method: 'DELETE' });

// ── Заказы ────────────────────────────────────────────────────────────────────

export const createOrder = (coffeeShopId: string) =>
  req<ApiResponse<OrderCreated>>('/api/v1/orders', {
    method: 'POST',
    body: JSON.stringify({ coffee_shop_id: coffeeShopId }),
  });

export const getOrders = () =>
  req<ApiResponse<OrderShort[]>>('/api/v1/orders');

// Этап 22: проверка повторного заказа на точку в текущий операционный день.
export interface TodayOrderCheck {
  has_order_today: boolean;
  public_number: string | null;
  operational_date: string;
}

export const checkTodayOrder = (coffeeShopId: string) =>
  req<ApiResponse<TodayOrderCheck>>(`/api/v1/orders/today?coffee_shop_id=${coffeeShopId}`);

// ── Депозиты (блок B, docs/deposits-spec.md) ──────────────────────────────────
// Заказы оплачиваются с депозита кофейни: резерв при оформлении, списание при
// отгрузке. Счёт на оплату заказа больше не выставляется — вместо него клиент
// сам выставляет «Счёт на пополнение депозита» и получает его временной ссылкой
// (в Telegram WKWebView <a download> не работает, а файлы в чат не шлём по
// решению заказчика — только ссылки).

export const getDepositMe = () =>
  req<ApiResponse<DepositAccount[]>>('/api/v1/deposits/me');

export const getDepositTransactions = (coffeeShopId: string) =>
  req<ApiResponse<DepositTransaction[]>>(`/api/v1/deposits/accounts/${coffeeShopId}/transactions`);

export const createDepositInvoice = (coffeeShopId: string, amount: number) =>
  req<ApiResponse<DepositInvoice>>('/api/v1/deposits/invoices', {
    method: 'POST',
    body: JSON.stringify({ coffee_shop_id: coffeeShopId, amount: amount.toFixed(2) }),
  });

export const getDepositInvoices = (coffeeShopId?: string) =>
  req<ApiResponse<DepositInvoice[]>>(
    `/api/v1/deposits/invoices${coffeeShopId ? `?coffee_shop_id=${coffeeShopId}` : ''}`,
  );

export const sendDepositInvoiceLink = (invoiceId: string, channel: 'telegram' | 'email') =>
  req<ApiResponse<{ sent: boolean; channel: string; url: string; expires_at: string | null }>>(
    `/api/v1/deposits/invoices/${invoiceId}/send-link`,
    { method: 'POST', body: JSON.stringify({ channel }) },
  );

// Прямое скачивание PDF в обычном браузере (вне Telegram): blob с Bearer-токеном.
export async function downloadDepositInvoice(invoiceId: string, number: string): Promise<void> {
  const res = await fetch(`/api/v1/deposits/invoices/${invoiceId}/pdf`, { headers: headers() });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${text}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Счёт_${number}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── Приёмка и претензии (блок E, docs/deposits-spec.md раздел 14) ─────────────

export const getOrder = (orderId: string) =>
  req<ApiResponse<OrderDetail>>(`/api/v1/orders/${orderId}`);

// «✅ Принять»: заказ закрывается, приёмка окончательна.
export const acceptOrderDelivery = (orderId: string) =>
  req<ApiResponse<OrderShort>>(`/api/v1/orders/${orderId}/accept`, { method: 'POST' });

export const getOrderClaim = (orderId: string) =>
  req<ApiResponse<Claim | null>>(`/api/v1/orders/${orderId}/claim`);

// «⚠️ Есть расхождения»: multipart — JSON в поле payload + до 5 фото в photos.
export async function fileOrderClaim(
  orderId: string,
  payload: ClaimFilePayload,
  photos: File[],
): Promise<ApiResponse<Claim>> {
  const form = new FormData();
  form.append('payload', JSON.stringify(payload));
  for (const f of photos) form.append('photos', f, f.name || 'photo.jpg');
  const h = headers();
  delete h['Content-Type']; // границу multipart выставит браузер
  const res = await fetch(`/api/v1/orders/${orderId}/claims`, { method: 'POST', headers: h, body: form });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${text}`);
  }
  return res.json() as Promise<ApiResponse<Claim>>;
}

// ── Акты сверки по депозиту (блок F, docs/deposits-spec.md раздел 15) ─────────

export const getActEntities = () =>
  req<ApiResponse<ActEntity[]>>('/api/v1/deposits/acts/entities');

export const getDepositActs = (legalEntityId?: string) =>
  req<ApiResponse<DepositAct[]>>(`/api/v1/deposits/acts${legalEntityId ? `?legal_entity_id=${legalEntityId}` : ''}`);

export const createDepositAct = (payload: { legal_entity_id: string; period_from: string; period_to: string; coffee_shop_ids?: string[] | null }) =>
  req<ApiResponse<DepositAct>>('/api/v1/deposits/acts', { method: 'POST', body: JSON.stringify(payload) });

export const getDepositAct = (actId: string) =>
  req<ApiResponse<DepositAct>>(`/api/v1/deposits/acts/${actId}`);

export const sendDepositActLink = (actId: string, channel: 'telegram' | 'email') =>
  req<ApiResponse<{ sent: boolean; channel: string; url: string; expires_at: string | null }>>(
    `/api/v1/deposits/acts/${actId}/send-link`,
    { method: 'POST', body: JSON.stringify({ channel }) },
  );

// PDF акта в обычном браузере: blob с Bearer-токеном.
export async function downloadDepositAct(actId: string, number: string): Promise<void> {
  const res = await fetch(`/api/v1/deposits/acts/${actId}/pdf`, { headers: headers() });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${text}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Акт_сверки_${number}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── Блок «Уведомления в приложении» ──────────────────────────────────────────
export const getNotifications = (limit = 50) =>
  req<ApiResponse<NotificationList>>(`/api/v1/notifications?limit=${limit}`);

export const getUnreadCount = () =>
  req<ApiResponse<{ unread: number }>>('/api/v1/notifications/unread-count');

export const markNotificationRead = (id: string) =>
  req<ApiResponse<{ ok: boolean; unread: number }>>(`/api/v1/notifications/${id}/read`, { method: 'POST' });

export const markAllNotificationsRead = () =>
  req<ApiResponse<{ marked: number; unread: number }>>('/api/v1/notifications/read-all', { method: 'POST' });

export const getNotificationPrefs = () =>
  req<ApiResponse<{ kinds: NotificationKindPrefs[] }>>('/api/v1/notifications/prefs');

export const saveNotificationPrefs = (prefs: Record<string, Partial<Record<NotificationChannel, boolean>>>) =>
  req<ApiResponse<{ kinds: NotificationKindPrefs[] }>>('/api/v1/notifications/prefs', {
    method: 'PUT',
    body: JSON.stringify({ prefs }),
  });

export type { AppNotification };

// ── Привязка Telegram к веб-аккаунту (миграция 021) ─────────────────────────
export const createTelegramLinkCode = () =>
  req<ApiResponse<TelegramLinkCode>>('/api/v1/auth/telegram-link/code', { method: 'POST' });

// Из Mini App (свежий Telegram-аккаунт): ввести код веб-аккаунта → токен уже для него.
export const claimTelegramLink = (code: string) =>
  req<ApiResponse<AuthResponse>>('/api/v1/auth/telegram-link/claim', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
